// ActionButton.jsx
import "bootstrap/dist/css/bootstrap.min.css";
import { FaPencilAlt } from "react-icons/fa";
import React, { useState, useRef, useEffect } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { Link } from "react-router-dom";
import ProcedureModel from "./addprocedure";
import AddExternallyLink from "./addExternallyLink";
import Edittask from "./editTask";
import CloseCase from "./closeCase";
import ShowData from "./showData";
import AddOutsourceShipmentTracking from "./addOutsourceShipmentTracking";
import ChangeOnHold from "./changeOnHold";
import InternalRemakeCase from "./internalRemakeCase";
import AddExternalycase from "./addExternalyCase";
import CancelCaseModal from "./CancelCaseModal";
import DraftInvoice from "../draftInvoice";
import ChangeAccount from "../changeAccount";

function ActionButton({ caseId, caseNumber, initialArrived, onTryInUpdate, onStatusUpdate, currentCase }) {
  const [showInternalModal, setShowInternalModal] = useState(false);
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [showDataModel, setShowDataModel] = useState(false);
  const [showCloseModel, setShowCloseModel] = useState(false);
  const [showExternallyData, setShowExternallyData] = useState(false);
  const [showEditTask, setShowEditTask] = useState(false);
  const [showExternallyLink, setShowExternallyLink] = useState(false);
  const [showAddProcedure, setShowAddProcedure] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showChangeAccountModal, setShowChangeAccountModal] = useState(false);
  const [isArrived, setIsArrived] = useState(initialArrived);
  const [isTryIn, setIsTryIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [visiblePages, setVisiblePages] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showDraftInvoice, setShowDraftInvoice] = useState(false);

  const disabledStyle = {
    padding: "8px 16px",
    color: "grey",
    pointerEvents: "none",
    opacity: 0.6,
  };

  // Function to get user data from localStorage
  const getUserData = () => {
    try {
      // Try different possible keys in localStorage
      const possibleKeys = ['user', 'userData', 'auth', 'currentUser', 'labUser', 'labUserData'];
      
      for (const key of possibleKeys) {
        const storedData = localStorage.getItem(key);
        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            
            // Check for userRole in different possible locations
            const userRole = 
              parsed.userRole || 
              parsed.role || 
              parsed.data?.userRole || 
              parsed.data?.role ||
              parsed.user?.userRole ||
              parsed.user?.role;
            
            if (userRole) {
              console.log(`Found userRole in key "${key}":`, userRole);
              return {
                userRole: userRole,
                userId: parsed.userId || parsed.id || parsed.data?.userId || parsed.data?.id
              };
            }
          } catch (e) {
            console.warn(`Could not parse localStorage key "${key}":`, e);
            continue;
          }
        }
      }
      
      console.error('User role not found in localStorage');
      return { userRole: null, userId: null };
    } catch (error) {
      console.error('Error getting user data from localStorage:', error);
      return { userRole: null, userId: null };
    }
  };

  useEffect(() => {
    const userData = getUserData();
    const adminStatus = userData.userRole === "Admin" || userData.userRole === "admin";
    
    setUserRole(userData.userRole);
    setIsAdmin(adminStatus);

    // If not admin, fetch assigned pages
    if (!adminStatus && userData.userId) {
      const fetchPermissions = async () => {
        try {
          const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/page/user/${userData.userId}/pages`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          
          if (!response.ok) {
            throw new Error("Failed to fetch permissions");
          }
          
          const data = await response.json();
          
          if (data && Array.isArray(data)) {
            // Extract page names from permissions
            const pageNames = data.map((perm) => perm.page?.name || perm.pageName).filter(Boolean);
            setVisiblePages(pageNames);
          }
        } catch (error) {
          console.error("Error fetching permissions:", error);
        }
      };
      fetchPermissions();
    } else if (adminStatus) {
      // If admin, show all pages - you can either:
      // 1. Fetch all pages from API and set them as visible
      // 2. Or use a predefined list of all page names
      // 3. Or handle it differently in the render logic
      
      // Option 1: Fetch all pages (if you have an API for that)
      // fetchAllPages();
      
      // Option 2: For now, we'll handle it in the render logic
      // We don't need to set visiblePages for admin since isAdmin=true
    }
  }, []);

  // Function to check if a page should be visible
  const shouldShowPage = (pageName) => {
    if (isAdmin) {
      return true; // Admin can see all pages
    }
    return visiblePages.includes(pageName);
  };

  useEffect(() => {
    const fetchCaseStatus = async () => {
      if (!caseId) {
        console.error("Error: Invalid caseId", caseId);
        setIsLoading(false);
        return;
      }
      try {
        const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/cases/${caseId}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to fetch case data");
        }
        const caseData = await response.json();
        setIsArrived(!!caseData.arrive_date);
        setIsTryIn(!!caseData.try_in);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching case status:", error);
        setIsLoading(false);
      }
    };
    fetchCaseStatus();
  }, [caseId]);

  const handleToggleArrive = async () => {
    if (!caseId) {
      console.error("Error: Invalid caseId", caseId);
      return;
    }
    try {
      setIsLoading(true);
      const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/cases/${caseId}/arrive-status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ arrived: !isArrived }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update arrive status");
      }
      const updatedCase = await response.json();

      setIsArrived(!isArrived);
      setIsLoading(false);
    } catch (error) {
      console.error("Error updating arrive status:", error);
      alert(`Error updating arrive status: ${error.message}`);
      setIsLoading(false);
    }
  };

  const handleMarkAsTryIn = async () => {
    if (!caseId) {
      console.error("Error: Invalid caseId", caseId);
      return;
    }
    const newTryIn = !isTryIn;
    try {
      const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/cases/${caseId}/try-in`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ try_in: newTryIn }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to update Try In status");
      }
      const updatedCase = await response.json();

      setIsTryIn(newTryIn);
      onTryInUpdate(newTryIn);
      alert(newTryIn ? "Case marked as Try In successfully" : "Case unmarked as Try In successfully");
    } catch (error) {
      console.error("Error updating Try In status:", error);
      alert(`Error updating Try In status: ${error.message}`);
    }
  };

  const fileInputRef = useRef(null);
  const handleUploadClick = () => fileInputRef.current.click();
  const handleFileChange = (event) => console.log(event.target.files);

  const handleCancelCase = async (reason) => {
    if (!caseNumber) {
      console.error("Error: Invalid caseNumber", caseNumber);
      alert("Error: Case number is missing");
      return;
    }
    try {
      const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/cases/cancel/${caseNumber}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ cancellationReason: reason }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to cancel case");
      }
      const updatedCase = await response.json();
      onStatusUpdate(updatedCase.case);
      alert("Case canceled successfully");
      window.location.href = "/cases-cancelled-report";
    } catch (error) {
      console.error("Error canceling case:", error);
      alert(`Error canceling case: ${error.message}`);
    }
  };

  const handleCancelAndCloneCase = () => {
    if (!caseNumber) {
      console.error("Error: Invalid caseNumber", caseNumber);
      return;
    }
    if (window.confirm("Are you sure you want to cancel and clone this case?")) {
      window.location.href = "/accountDetails";
    }
  };

  const handleChangeAccount = async (newAccountData) => {
    try {
      const response = await fetch(`https://hms.backend.hiragroupcloud.com/api/cases/${caseId}/change-account`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(newAccountData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to change account");
      }

      const updatedCase = await response.json();
      onStatusUpdate(updatedCase.case);
      alert("Account changed successfully");
      setShowChangeAccountModal(false);
    } catch (error) {
      console.error("Error changing account:", error);
      alert(`Error changing account: ${error.message}`);
    }
  };

  // Handle account change confirmation
  const handleAccountChangeConfirm = (data) => {
    // Update your UI with new data
    if (onStatusUpdate) {
      onStatusUpdate(data.updatedCase);
    }
    setShowChangeAccountModal(false);
    alert("Account and prices updated successfully!");
  };

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle
          variant="primary"
          className="d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "#004aad",
            borderColor: "#004aad",
            color: "#ffffff",
            padding: "8px 16px",
            fontSize: "14px",
            borderRadius: "5px",
            transition: "all 0.3s ease",
          }}
          id="dropdown-basic"
          disabled={isLoading}
          onMouseOver={(e) => (e.target.style.backgroundColor = "#003a8c")}
          onMouseOut={(e) => (e.target.style.backgroundColor = "#004aad")}
        >
          <FaPencilAlt className="me-2" /> {isLoading ? "Loading..." : "Actions"}
        </Dropdown.Toggle>
        <Dropdown.Menu
          className="shadow-lg"
          style={{
            minWidth: "200px",
            borderRadius: "5px",
            padding: "5px 0",
            fontSize: "14px",
            backgroundColor: "#ffffff",
            border: "1px solid #dee2e6",
          }}
        >
          {/* Arrive Case */}
          {shouldShowPage("Arrive Case") && (
            isLoading ? (
              <Dropdown.Item className="text-muted" disabled style={{ padding: "8px 16px" }}>
                Loading...
              </Dropdown.Item>
            ) : (
              <Dropdown.Item
                onClick={handleToggleArrive}
                disabled={isLoading}
                style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
                onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
                onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
              >
                {isArrived ? "Un-Arrive Case" : "Arrive Case"}
              </Dropdown.Item>
            )
          )}

          {/* Internal Remake Case */}
          {shouldShowPage("Internal Remake Case") && (
            <Dropdown.Item 
              onClick={() => setShowInternalModal(true)}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Internal Remake Case
            </Dropdown.Item>
          )}

          {/* Mark As Try In */}
          {shouldShowPage("Mark As Try In") && (
            <Dropdown.Item
              onClick={handleMarkAsTryIn}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              {isTryIn ? "Mark As Not Try In" : "Mark As Try In"}
            </Dropdown.Item>
          )}

          {/* Change On Hold Status */}
          {shouldShowPage("Change On Hold Status") && (
            <Dropdown.Item
              onClick={() => setShowChangeModal(true)}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Change On Hold Status
            </Dropdown.Item>
          )}

          {/* Add Note */}
          {shouldShowPage("Add Note") && (
            <Dropdown.Item 
              onClick={() => setShowNoteModal(true)}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Add Note
            </Dropdown.Item>
          )}

          {/* Add Outsource Shipment Tracking */}
          {shouldShowPage("Add Outsource Shipment Tracking") && (
            <Dropdown.Item 
              onClick={() => setShowSourceModal(true)}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Add Outsource Shipment Tracking
            </Dropdown.Item>
          )}

          {/* Show Data */}
          {shouldShowPage("Show Data") && (
            <Dropdown.Item 
              onClick={() => setShowDataModel(true)}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Show Data
            </Dropdown.Item>
          )}

          {/* Close/Invoice Case */}
          {shouldShowPage("Close/Invoice Case") && (
            <Dropdown.Item 
              onClick={() => setShowCloseModel(true)}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Close/Invoice Case
            </Dropdown.Item>
          )}

          {/* Draft Invoice */}
          {shouldShowPage("draft invoice") && (
            <Dropdown.Item
              onClick={() => setShowDraftInvoice(true)}
              style={{ padding: "8px 16px", color: "#004aad", fontWeight: "700" }}
            >
              Draft Invoice
            </Dropdown.Item>
          )}

          <hr className="dropdown-divider my-1" />

          {/* Upload Files */}
          {shouldShowPage("Upload Files") && (
            <Dropdown.Item 
              onClick={handleUploadClick}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Upload Files
            </Dropdown.Item>
          )}

          {/* Add Externally Linked File */}
          {shouldShowPage("Add Externally Linked File") && (
            <Dropdown.Item 
              onClick={() => setShowExternallyLink(true)}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Add Externally Linked File
            </Dropdown.Item>
          )}

          <hr className="dropdown-divider my-1" />

          {/* Edit Task */}
          {shouldShowPage("Edit Task") && (
            <Dropdown.Item 
              onClick={() => setShowEditTask(true)}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Edit Task
            </Dropdown.Item>
          )}

          {/* Add Procedure */}
          {shouldShowPage("Add Procedure") && (
            <Dropdown.Item 
              onClick={() => setShowAddProcedure(true)}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Add Procedure
            </Dropdown.Item>
          )}

          {/* Add Externally Linked Case */}
          {shouldShowPage("Add Externally Linked Case") && (
            <Dropdown.Item 
              onClick={() => setShowExternallyData(true)}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Add Externally Linked Case
            </Dropdown.Item>
          )}

          <hr className="dropdown-divider my-1" />

          {/* Print Work Ticket */}
          {shouldShowPage("Print Work Ticket") && (
            <Dropdown.Item
              as={Link}
              to={`/workticket/${caseId}`}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Print Work Ticket
            </Dropdown.Item>
          )}

          {/* Print Work Ticket Sticker */}
          {shouldShowPage("Print Work Ticket Sticker") && (
            <Dropdown.Item
              as={Link}
              to={`/sticker/${caseId}`}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Print Work Ticket Sticker
            </Dropdown.Item>
          )}

          <hr className="dropdown-divider my-1" />

          {/* Cancel Case */}
          {shouldShowPage("Cancel Case") && (
            <Dropdown.Item
              onClick={() => setShowCancelModal(true)}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Cancel Case
            </Dropdown.Item>
          )}

          {/* Cancel and Clone Case */}
          {shouldShowPage("Cancel and Clone Case") && (
            <Dropdown.Item
              onClick={handleCancelAndCloneCase}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Cancel and Clone Case
            </Dropdown.Item>
          )}

          {/* Clone Case */}
          {shouldShowPage("Clone Case") && (
            <Dropdown.Item disabled style={disabledStyle}>
              Clone Case
            </Dropdown.Item>
          )}

          {/* CHANGE ACCOUNT OPTION */}
          <hr className="dropdown-divider my-1" />
          {shouldShowPage("Change Account") && (
            <Dropdown.Item
              onClick={() => setShowChangeAccountModal(true)}
              style={{ padding: "8px 16px", color: "#004aad", transition: "background-color 0.2s", fontWeight: "700" }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "transparent")}
            >
              Change Account
            </Dropdown.Item>
          )}
        </Dropdown.Menu>
      </Dropdown>

      {/* Modals */}
      <InternalRemakeCase 
        showModal={showInternalModal} 
        handleClose={() => setShowInternalModal(false)} 
      />
      <ChangeOnHold 
        caseId={caseId} 
        showModal={showChangeModal} 
        handleClose={() => setShowChangeModal(false)} 
      />
      <AddOutsourceShipmentTracking 
        showModal={showSourceModal} 
        handleClose={() => setShowSourceModal(false)} 
      />
      <ShowData 
        showModal={showDataModel} 
        handleClose={() => setShowDataModel(false)} 
      />
      <CloseCase 
        showModal={showCloseModel} 
        handleClose={() => setShowCloseModel(false)} 
      />
      <AddExternalycase 
        showModal={showExternallyData} 
        handleClose={() => setShowExternallyData(false)} 
      />
      <Edittask 
        showModal={showEditTask} 
        handleClose={() => setShowEditTask(false)} 
      />
      <ProcedureModel 
        showModal={showAddProcedure} 
        handleClose={() => setShowAddProcedure(false)} 
      />
      <AddExternallyLink 
        showModal={showExternallyLink} 
        handleClose={() => setShowExternallyLink(false)} 
      />

      <CancelCaseModal
        show={showCancelModal}
        handleClose={() => setShowCancelModal(false)}
        handleConfirm={handleCancelCase}
        caseNumber={caseNumber}
      />

      <ChangeAccount
        show={showChangeAccountModal}
        handleClose={() => setShowChangeAccountModal(false)}
        caseNumber={caseNumber}
        handleConfirm={handleAccountChangeConfirm}
      />

      {showDraftInvoice && (
        <DraftInvoice
          show={showDraftInvoice}
          onHide={() => setShowDraftInvoice(false)}
          caseId={caseId}
          caseNumber={caseNumber}
        />
      )}
      
      <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleFileChange} />
    </>
  );
}

export default ActionButton;